import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdAdd, MdExpandMore } from 'react-icons/md';

function escapeRegExp(text) {
  return String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function optionValue(opt) {
  if (opt == null) return '';
  if (typeof opt !== 'object') return String(opt);
  if (opt.value != null && opt.value !== '') return String(opt.value);
  if (opt.id != null && opt.id !== '') return String(opt.id);
  return '';
}

export function optionLabel(opt) {
  if (opt == null) return '';
  if (typeof opt !== 'object') return String(opt);
  return String(opt.label || opt.name || opt.value || opt.id || '');
}

function Highlight({ text, query }) {
  const str = String(text || '');
  const q = String(query || '').trim();
  if (!q) return str;
  const parts = str.split(new RegExp(`(${escapeRegExp(q)})`, 'gi'));
  return parts.map((part, idx) =>
    part.toLowerCase() === q.toLowerCase() ? (
      <span key={idx} className="ea-odoo-ac__mark">
        {part}
      </span>
    ) : (
      <React.Fragment key={idx}>{part}</React.Fragment>
    )
  );
}

export default function OdooAutocomplete({
  value = '',
  options = [],
  onChange,
  onSearch,
  required = false,
  disabled = false,
  placeholder = '',
  className = '',
  emptyText = 'No matching results',
  onCreate,
  creating = false,
  'aria-label': ariaLabel,
}) {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [searchPending, setSearchPending] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);

  const normalized = useMemo(
    () =>
      (options || []).map((opt) => ({
        value: optionValue(opt),
        label: optionLabel(opt),
        raw: opt,
      })),
    [options]
  );

  const selected = useMemo(
    () => normalized.find((opt) => opt.value === String(value ?? '')) || null,
    [normalized, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || (selected && q === selected.label.toLowerCase())) return normalized;
    return normalized.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [normalized, query, selected]);

  const createQuery = query.trim();
  const canCreate =
    Boolean(onCreate && createQuery) &&
    !searchPending &&
    !normalized.some((opt) => opt.label.toLowerCase() === createQuery.toLowerCase());

  useEffect(() => {
    if (!open) setQuery(selected ? selected.label : '');
  }, [selected, open]);

  useEffect(() => {
    if (!open || !onSearch) {
      setSearchPending(false);
      return undefined;
    }
    const q = query.trim();
    if (selected && q.toLowerCase() === selected.label.toLowerCase()) {
      setSearchPending(false);
      return undefined;
    }
    let cancelled = false;
    setSearchPending(true);
    const t = setTimeout(async () => {
      try {
        await onSearch(q);
      } finally {
        if (!cancelled) setSearchPending(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, open, onSearch, selected]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  const commit = (opt) => {
    if (opt) {
      onChange?.(opt.value, opt.raw);
      setQuery(opt.label);
    } else {
      onChange?.('', null);
      setQuery('');
    }
    setOpen(false);
  };

  const closeAndCommit = () => {
    const q = query.trim().toLowerCase();
    if (!q) {
      commit(null);
      return;
    }
    const exact = normalized.find((opt) => opt.label.toLowerCase() === q);
    if (exact) {
      commit(exact);
      return;
    }
    if (filtered.length === 1) {
      commit(filtered[0]);
      return;
    }
    setQuery(selected ? selected.label : query);
    setOpen(false);
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    const place = () => {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const gap = 2;
      const maxH = 240;
      const width = Math.max(rect.width, 180);
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const openUp = spaceBelow < 120 && rect.top > spaceBelow;
      setMenuStyle({
        position: 'fixed',
        left,
        width,
        maxHeight: maxH,
        zIndex: 5000,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + gap, top: 'auto' }
          : { top: rect.bottom + gap, bottom: 'auto' }),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, filtered.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(event.target);
      const inMenu = listRef.current && listRef.current.contains(event.target);
      if (!inWrap && !inMenu) closeAndCommit();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  });

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open, filtered]);

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (open && filtered[activeIndex]) {
        commit(filtered[activeIndex]);
      } else if (open && canCreate) {
        setOpen(false);
        onCreate?.(createQuery);
      } else {
        closeAndCommit();
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setQuery(selected ? selected.label : '');
      setOpen(false);
    }
  };

  return (
    <div
      className={`ea-odoo-ac${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}${
        className ? ` ${className}` : ''
      }`}
      ref={wrapRef}
    >
      <input
        ref={inputRef}
        type="text"
        className="ea-odoo-ac__input"
        value={open ? query : selected ? selected.label : query}
        placeholder={placeholder}
        disabled={disabled}
        required={required && !value}
        autoComplete="off"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-autocomplete="list"
        role="combobox"
        onFocus={(e) => {
          if (disabled) return;
          setOpen(true);
          setQuery(selected ? selected.label : query);
          e.target.select();
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      <MdExpandMore className="ea-odoo-ac__caret" aria-hidden />
      {open && !disabled && menuStyle
        ? createPortal(
            <ul
              className="ea-odoo-ac__menu ea-odoo-ac__menu--portal"
              role="listbox"
              ref={listRef}
              style={menuStyle}
            >
              {canCreate ? (
                <li className="ea-odoo-ac__create-row">
                  <button
                    type="button"
                    className="ea-odoo-ac__item ea-odoo-ac__item--create"
                    disabled={creating}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setOpen(false);
                      onCreate?.(createQuery);
                    }}
                  >
                    <MdAdd aria-hidden />
                    {creating ? 'Creating...' : 'Create ' + createQuery}
                  </button>
                </li>
              ) : null}
              {filtered.length === 0 ? (
                <li className="ea-odoo-ac__empty">{emptyText}</li>
              ) : (
                filtered.map((opt, idx) => (
                  <li key={`${opt.value}-${idx}`}>
                    <button
                      type="button"
                      role="option"
                      data-active={idx === activeIndex ? 'true' : undefined}
                      aria-selected={opt.value === String(value ?? '')}
                      className={`ea-odoo-ac__item${idx === activeIndex ? ' is-active' : ''}${
                        opt.value === String(value ?? '') ? ' is-selected' : ''
                      }`}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => commit(opt)}
                    >
                      <Highlight
                        text={opt.label}
                        query={
                          selected &&
                          query.trim().toLowerCase() === selected.label.toLowerCase()
                            ? ''
                            : query
                        }
                      />
                    </button>
                  </li>
                ))
              )}
            </ul>,
            document.body
          )
        : null}
    </div>
  );
}
